import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, CheckCircle2, XCircle, AlertTriangle, Upload, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';


export function EBCredentialsPanel() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [error, setError] = useState(null);

  // Form state
  const [pemText, setPemText] = useState('');
  const [applicationId, setApplicationId] = useState('');
  const [environment, setEnvironment] = useState('sandbox');

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/admin/diagnostics/eb-credentials-status');
      setCredentials(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPemText(ev.target.result);
      toast.success(`Loaded ${file.name}`);
    };
    reader.onerror = () => toast.error('Failed to read file');
    reader.readAsText(file);
  };

  const handleSave = async () => {
    if (!pemText.trim()) {
      toast.error('PEM key is required');
      return;
    }
    if (!applicationId.trim()) {
      toast.error('Application ID is required');
      return;
    }

    // Confirm overwrite if credentials already exist
    if (credentials?.credentials?.length > 0) {
      const existing = credentials.credentials.find((c) => c.environment === environment);
      if (existing) {
        const ok = window.confirm(
          `Credentials already exist for ${environment}. Re-uploading will replace them. Continue?`
        );
        if (!ok) return;
      }
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('pem_text', pemText);
      formData.append('application_id', applicationId.trim());
      formData.append('environment', environment);

      await api.post('/admin/integrations/enablebanking/credentials', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Enable Banking credentials saved for ${environment}`);
      setPemText('');
      setApplicationId('');
      await fetchStatus();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <KeyRound className="h-5 w-5" />
          Enable Banking Credentials
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Upload the PEM private key and application ID for Enable Banking.
          Credentials are stored per environment (production/sandbox).
        </p>

        {/* Stored credentials */}
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading credential status...
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        ) : credentials?.credentials?.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Stored Credentials</p>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-3 py-1.5 font-medium">Environment</th>
                    <th className="text-left px-3 py-1.5 font-medium">Fingerprint</th>
                    <th className="text-left px-3 py-1.5 font-medium">Key Bits</th>
                    <th className="text-left px-3 py-1.5 font-medium">Stored At</th>
                  </tr>
                </thead>
                <tbody>
                  {credentials.credentials.map((cred) => (
                    <tr key={cred.environment} className="border-b last:border-0">
                      <td className="px-3 py-1.5">
                        <Badge
                          variant={cred.environment === 'production' ? 'default' : 'outline'}
                          className="text-[10px]"
                        >
                          {cred.environment}
                        </Badge>
                      </td>
                      <td className="px-3 py-1.5 font-mono" title={cred.fingerprint}>
                        {cred.fingerprint
                          ? cred.fingerprint.length > 20
                            ? cred.fingerprint.slice(0, 20) + '...'
                            : cred.fingerprint
                          : '-'}
                      </td>
                      <td className="px-3 py-1.5 tabular-nums">{cred.key_bits ?? '-'}</td>
                      <td className="px-3 py-1.5 tabular-nums">
                        {cred.stored_at
                          ? new Date(cred.stored_at).toLocaleString()
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <XCircle className="h-4 w-4 text-red-500" />
            No credentials stored yet.
          </div>
        )}

        {/* Upload form */}
        <div className="border rounded-lg p-4 space-y-3">
          <p className="text-xs font-medium">Upload Credentials</p>

          {/* PEM textarea */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">PEM Private Key</label>
            <textarea
              className="w-full border rounded-md px-3 py-2 text-xs font-mono bg-muted/20 min-h-[120px] resize-y focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Paste PEM-encoded RSA private key here"
              value={pemText}
              onChange={(e) => setPemText(e.target.value)}
            />
            <div className="flex items-center gap-2">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".pem,.key"
                  className="hidden"
                  onChange={handleFileUpload}
                />
                <span className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 hover:underline">
                  <Upload className="h-3 w-3" />
                  or upload PEM file
                </span>
              </label>
            </div>
          </div>

          {/* Application ID */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Application ID</label>
            <input
              type="text"
              className="w-full border rounded-md px-3 py-2 text-xs bg-muted/20 focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="e.g. abc123-def456-..."
              value={applicationId}
              onChange={(e) => setApplicationId(e.target.value)}
            />
          </div>

          {/* Environment */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Environment</label>
            <select
              className="w-full border rounded-md px-3 py-2 text-xs bg-muted/20 focus:outline-none focus:ring-2 focus:ring-ring"
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
            >
              <option value="sandbox">Sandbox</option>
              <option value="production">Production</option>
            </select>
          </div>

          {/* Save button */}
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || !pemText.trim() || !applicationId.trim()}
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
            Save Credentials
          </Button>
        </div>

        {/* Refresh button */}
        <Button
          variant="outline"
          size="sm"
          onClick={fetchStatus}
          disabled={loading}
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
          Refresh
        </Button>
      </CardContent>
    </Card>
  );
}
